import { Route } from '@angular/router';
import { FrontpageComponent } from './pages/frontpage/frontpage.component';
import {
  ROUTE_CHANGE_TYPE,
  ROUTE_CONFIRMATION,
  ROUTE_CURRENT_CONTRACT_PARTNER,
  ROUTE_DATENSCHUTZ,
  ROUTE_HOME,
  ROUTE_IMPRESSUM,
  ROUTE_LOGOUT_SUCCESS,
  ROUTE_MAINTENANCE,
  ROUTE_METER_POINT_CHANGE_REQUEST,
  ROUTE_NEW_CONTRACT_PARTNER,
  ROUTE_PDF_UPLOAD,
  ROUTE_PRODUCTS_AND_DATE,
  ROUTE_RECHNUNG_REKLAMIEREN_WIZARD,
  ROUTE_REGISTRATION_WIZARD,
  ROUTE_SUMMARY,
  ROUTE_TERMS_OF_USE,
  ROUTE_UMMELDUNG_WIZARD,
  ROUTE_WELCOME_WIZARD,
} from './app.constants';
import { authGuard } from '@shared-ikp/auth/guard/auth.guard';
import { TermsOfUsePageComponent } from './pages/terms-of-use/terms-of-use-page.component';
import { trackingGuard } from './guards/tracking.guard';
import { MeterPointChangeRequestComponent } from './pages/meter-point-change-request/meter-point-change-request.component';
import { SelectChangeTypeComponent } from './pages/meter-point-change-request/01_select_change_type/select_change_type.component';
import { ProductAndDateComponent } from './pages/meter-point-change-request/02_product_and_date/product-and-date.component';
import { CurrentContractPartnerComponent } from './pages/meter-point-change-request/03_current_contract_partner/current_contract_partner.component';
import { PdfUploadComponent } from './pages/meter-point-change-request/05_2_pdf_upload/pdf-upload.component';
import { SummaryComponent } from './pages/meter-point-change-request/05_summary/summary.component';
import { ConfirmationComponent } from './pages/meter-point-change-request/06_confirmation/confirmation.component';
import { featureFlagGuard } from './guards/feature-flag.guard';
import { FeatureFlag } from './model/feature-flag.model';
import { NewContractPartnerComponent } from './pages/meter-point-change-request/04_new_contract_partner/new-contract-partner.component';
import { userStatusGuard } from './guards/userStatus.guard';
import { RegistrationWizardStateService } from './pages/registration-wizard/registration-wizard.state.service';
import { HeadlineService } from '@mwe/ui/components';
import { CookieRedirectGuard } from './guards/cookie-redirect.guard';
import { UmmeldungWizardStateService } from './pages/ummeldung-wizard/ummeldung.wizard.state.service';
import { UmmeldungStore } from './pages/ummeldung-wizard/store';
import { IkpMaintenance } from './pages/maintenance/ikp-maintenance.component';
import { maintenanceGuard } from './guards/maintenance.guard';

const authenticatedMaintenanceGuards = [authGuard, maintenanceGuard];
const welcomeWizardGuards = [authGuard, userStatusGuard, maintenanceGuard];
const ummeldungWizardGuards = [authGuard, featureFlagGuard(FeatureFlag.UMMELDUNG_NEW), maintenanceGuard];
const meterPointChangeRequestGuards = [authGuard, featureFlagGuard(FeatureFlag.UMMELDUNG), maintenanceGuard];
const rechnungReklamierenWizardGuards = [authGuard, featureFlagGuard(FeatureFlag.RECHNUNG_REKLAMIEREN), maintenanceGuard];

export const appRoutes: Route[] = [
  {
    path: '',
    component: FrontpageComponent,
    canActivate: [trackingGuard, maintenanceGuard],
    children: [],
  },
  {
    path: ROUTE_LOGOUT_SUCCESS,
    component: FrontpageComponent,
    canActivate: [trackingGuard, maintenanceGuard],
  },
  {
    path: ROUTE_HOME,
    loadChildren: () => import('./pages/main/main.routes').then(mod => mod.MAIN_ROUTES),
    canActivate: authenticatedMaintenanceGuards,
    canActivateChild: [maintenanceGuard],
  },
  {
    path: ROUTE_TERMS_OF_USE,
    canActivate: [trackingGuard],
    component: TermsOfUsePageComponent,
  },
  {
    path: ROUTE_REGISTRATION_WIZARD,
    canActivate: [trackingGuard, ...authenticatedMaintenanceGuards],
    canActivateChild: authenticatedMaintenanceGuards,
    providers: [RegistrationWizardStateService, HeadlineService],
    loadChildren: () => import('./pages/registration-wizard/registration.wizard.module').then(mod => mod.RegistrationWizardModule),
  },
  {
    path: ROUTE_WELCOME_WIZARD,
    canActivate: [trackingGuard, ...welcomeWizardGuards],
    canActivateChild: welcomeWizardGuards,
    loadChildren: () => import('./pages/welcome-wizard/welcome.wizard.module').then(mod => mod.WelcomeWizardModule),
  },
  {
    path: ROUTE_UMMELDUNG_WIZARD,
    canActivate: [trackingGuard, ...ummeldungWizardGuards],
    canActivateChild: ummeldungWizardGuards,
    providers: [UmmeldungStore, UmmeldungWizardStateService],
    loadChildren: () => import('./pages/ummeldung-wizard/ummeldung.wizard').then(mod => mod.UmmeldungWizardModule),
  },
  {
    path: ROUTE_METER_POINT_CHANGE_REQUEST,
    component: MeterPointChangeRequestComponent,
    canActivate: [trackingGuard, ...meterPointChangeRequestGuards],
    canActivateChild: meterPointChangeRequestGuards,
    children: [
      { path: ROUTE_CHANGE_TYPE, component: SelectChangeTypeComponent },
      { path: ROUTE_PRODUCTS_AND_DATE, component: ProductAndDateComponent },
      { path: ROUTE_CURRENT_CONTRACT_PARTNER, component: CurrentContractPartnerComponent },
      { path: ROUTE_NEW_CONTRACT_PARTNER, component: NewContractPartnerComponent },
      { path: ROUTE_SUMMARY, component: SummaryComponent },
      { path: ROUTE_PDF_UPLOAD, component: PdfUploadComponent },
      { path: ROUTE_CONFIRMATION, component: ConfirmationComponent },
      { path: '**', redirectTo: ROUTE_CHANGE_TYPE, pathMatch: 'full' },
    ],
  },
  {
    path: ROUTE_RECHNUNG_REKLAMIEREN_WIZARD,
    canActivate: [trackingGuard, ...rechnungReklamierenWizardGuards],
    canActivateChild: rechnungReklamierenWizardGuards,
    loadChildren: () => import('./pages/rechnung-reklamieren/rechnung-reklamieren.module').then(mod => mod.RechnungReklamierenModule),
  },
  {
    path: ROUTE_IMPRESSUM,
    component: CookieRedirectGuard,
    canActivate: [CookieRedirectGuard],
  },
  {
    path: ROUTE_DATENSCHUTZ,
    component: CookieRedirectGuard,
    canActivate: [CookieRedirectGuard],
  },
  {
    path: ROUTE_MAINTENANCE,
    component: IkpMaintenance,
  },
  {
    path: '**',
    pathMatch: 'full',
    canActivate: [authGuard, userStatusGuard, maintenanceGuard],
    loadComponent: () => import('./pages/main/main.routes').then(mod => mod.MAIN_ROUTES.find(route => route.path === '').component),
  },
];
